import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropertyCard from '../components/common/PropertyCard';
import EnquiryForm from '../components/common/EnquiryForm';
import { getProperties } from '../services/api';
import './CityPage.css';

const CITY_DATA = {
  'delhi-ncr': {
    name: 'Delhi NCR',
    apiName: 'Delhi NCR',
    tagline: "India's Capital Region - Premium Real Estate Destination",
    desc: "Delhi NCR (National Capital Region) is one of India's most dynamic real estate markets, encompassing Delhi, Noida, Greater Noida, Gurgaon, Faridabad, and Ghaziabad. The region offers exceptional connectivity through an extensive metro network, world-class social infrastructure, and a thriving commercial ecosystem driven by Fortune 500 companies and government institutions.",
    img: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=1400&q=85',
    whyLabel: 'Why Delhi NCR?',
    whyTitle: 'A Premier Real Estate Destination',
    why: [
      "Largest consumer market in India",
      "Extensive metro connectivity across cities",
      "Top hospitals, schools and universities",
      "Strong rental yields of 3 to 5 percent per annum",
      "High NRI investment preference",
      "RERA-compliant project ecosystem",
    ],
    stats: [{ v: '200+', l: 'Projects' }, { v: '45L+', l: 'Avg. Price' }, { v: '4.2%', l: 'Rental Yield' }, { v: '12%', l: 'YoY Growth' }],
  },
  'dholera-sir': {
    name: 'Dholera SIR',
    apiName: 'Dholera SIR',
    tagline: "India's First Greenfield Smart City - The Investment of a Decade",
    desc: "Dholera Special Investment Region (SIR) is India's most ambitious urban development project, a greenfield smart city being built from scratch under the Delhi-Mumbai Industrial Corridor (DMIC). With a dedicated international airport, 6-lane expressway, high-speed rail connectivity, and government-backed infrastructure, Dholera offers land prices that are still a fraction of established markets, making it one of the highest-upside investment opportunities in India.",
    img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1400&q=85',
    whyLabel: 'Why Dholera SIR?',
    whyTitle: 'A Premier Investment Destination',
    why: [
      "Part of DMIC - India's flagship industrial corridor",
      "Upcoming Dholera International Airport",
      "Land prices significantly lower than Ahmedabad",
      "Government-guaranteed infrastructure development",
      "6-lane expressway connecting to Ahmedabad",
      "SEZ status with tax and business benefits",
    ],
    stats: [{ v: '80+', l: 'Projects' }, { v: '8L+', l: 'Avg. Price' }, { v: '25%', l: 'Projected Growth' }, { v: '920', l: 'Sq.km. Area' }],
  },
  ahmedabad: {
    name: 'Ahmedabad',
    apiName: 'Ahmedabad',
    tagline: "Gujarat's Financial Capital - A Booming Real Estate Market",
    desc: "Ahmedabad is the commercial and cultural capital of Gujarat and one of India's fastest-growing cities. As India's first UNESCO World Heritage City, Ahmedabad blends heritage with modernity. The city's robust industrial base, proximity to Dholera SIR, strong business community, and top-tier educational institutions make it one of India's most attractive real estate investment destinations with steady appreciation and high rental demand.",
    img: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1400&q=85',
    whyLabel: 'Why Ahmedabad?',
    whyTitle: 'A Growing Investment Hub',
    why: [
      "UNESCO World Heritage City",
      "India's first Heritage City",
      "Gateway to Dholera SIR investment zone",
      "Strong manufacturing and textile industry",
      "IIM Ahmedabad and top national institutions",
      "Booming luxury housing market",
    ],
    stats: [{ v: '120+', l: 'Projects' }, { v: '55L+', l: 'Avg. Price' }, { v: '5.1%', l: 'Rental Yield' }, { v: '18%', l: 'YoY Growth' }],
  },
};

const CityPage = () => {
  const { city } = useParams();
  const data = CITY_DATA[city] || CITY_DATA['delhi-ncr'];
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProperties({ city: data.apiName })
      .then(res => {
        const list = res.data?.data?.content || res.data?.data || res.data || [];
        setProperties(Array.isArray(list) ? list : []);
      })
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, [data.apiName]);

  const statCols = data.stats.length;

  return (
    <div>
      {/* Hero */}
      <div className="city-hero">
        <img src={data.img} alt={`Real estate in ${data.name}`} className="city-hero__img"/>
        <div className="city-hero__overlay"/>
        <div className="container city-hero__content">
          <span className="section-label" style={{color:'var(--primary)',marginBottom:'12px'}}>Real Estate in</span>
          <h1 className="city-hero__title">{data.name}</h1>
          <p className="city-hero__tagline">{data.tagline}</p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="city-stats-bar">
        <div className="container">
          <div className="city-stats-grid" style={{gridTemplateColumns:`repeat(${statCols},1fr)`}}>
            {data.stats.map((s,i) => (
              <div key={i} className="city-stat-item" style={{borderRight: i < statCols-1 ? '1px solid rgba(255,255,255,0.15)' : 'none'}}>
                <div className="city-stat-val">{s.v}</div>
                <div className="city-stat-lbl">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why + Enquiry */}
      <section className="city-why-section">
        <div className="container">
          <div className="city-why-grid">
            <div>
              <span className="section-label">{data.whyLabel}</span>
              <h2 className="section-title" style={{marginBottom:'16px'}}>{data.whyTitle}</h2>
              <div className="gold-divider"/>
              <p className="city-why-desc">{data.desc}</p>
              <div className="city-why-points">
                {data.why.map(w => (
                  <div key={w} className="city-why-point">
                    <span className="city-why-check">✓</span>
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="city-enquiry-col">
              <EnquiryForm propertyTitle={`Property Enquiry - ${data.name}`} variant="default"/>
            </div>
          </div>
        </div>
      </section>

      {/* Properties */}
      <section className="city-props-section">
        <div className="container">
          <div style={{marginBottom:'36px'}}>
            <span className="section-label">Our Listings</span>
            <h2 className="section-title" style={{marginBottom:0}}>Properties in {data.name}</h2>
          </div>
          {loading ? (
            <div style={{textAlign:'center',padding:'60px',color:'var(--text-3)'}}>Loading properties...</div>
          ) : properties.length > 0 ? (
            <div className="city-props-grid">
              {properties.map(p => <PropertyCard key={p.id} property={p}/>)}
            </div>
          ) : (
            <div style={{textAlign:'center',padding:'60px 20px',color:'var(--text-3)'}}>
              <p style={{fontSize:'16px',marginBottom:'8px'}}>No properties listed in {data.name} yet.</p>
              <p style={{fontSize:'14px'}}>Check back soon. We are actively adding new listings.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CityPage;
